import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { TkFakeTableProps } from "../../../type/componentsType/TkFakeTable";
import TkFakeTableLine from "../../atoms/TkFakeTableLine";
import { Controller, useForm } from "react-hook-form";
import TkButton from "../../atoms/TkButton";
import TkFormContextProvider, {
  TkFormContext,
} from "../../../context/tkFormContext";
import { Box } from "@mui/material";
import "./_style.scss";

/**
 * 画面基本構成部品（メイン）
 * リストのアイテムを使用してフェイクテーブルコンポーネントをレンダリングします。
 * @param {TkFakeTableProps} props - レンダリングするアイテムのリストを含むプロップスオブジェクト。
 * @return TkFakeTable 画面基本構成部品（メイン）
 */
export const TkFakeTable: React.FC<TkFakeTableProps> = forwardRef(
  (props, ref) => {
    return (
      <TkFormContextProvider>
        <TkFakeTableForm {...props} ref={ref} />
      </TkFormContextProvider>
    );
  }
);

export const TkFakeTableForm: React.FC<TkFakeTableProps> = forwardRef(
  (props, ref) => {
    const {
      list,
      submitButtonText = "機構へ提出する",
      showButton = true,
      onCheckAndSubmit,
      buttonGroup,
      ruleSchema,
      disabled,
      submitButtonGroup,
    } = props;

    const submitSource = useRef(0); // 提出元
    const submitFrom = useRef<HTMLFormElement>(null); // 提出フォームref

    const [errorMsgList, setErrorMsgList] = useState<any>({});
    const [temSaveFlagRef, setTemSaveFlagRef] = useState<any>(false); // バリエーションチェックを飛ばすフラグ
    const { formContext } = useContext(TkFormContext);

    const {
      handleSubmit,
      control,
      formState: { errors },
      clearErrors,
      getValues,
      setValue,
      register,
      unregister,
    } = useForm<any>({
      mode: "all",
      reValidateMode: "onChange",
    });

    // 对外暴露的方法
    useImperativeHandle(ref, () => {
      return {
        submit: (temSave?: boolean) => {
          submitSource.current = 0;
          preventSubmission(submitFrom.current, temSave, true);
        },
        getAllValue: () => {
          return getValues();
        },
      };
    });

    const initErrorScrollObserver = () => {
      const observer = new MutationObserver((mutationsList, observer) => {
        const errorElements = document.querySelectorAll(
          ".MuiFormHelperText-root.Mui-error"
        );

        if (errorElements.length > 0) {
          errorElements[0].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          observer.disconnect();
        } else {
          observer.disconnect();
        }
      });

      const config = { childList: true, subtree: true };
      observer.observe(document.body, config);
      return observer;
    };

    const preventSubmission = (
      e: any,
      temSave?: boolean,
      isRefSubmit?: boolean
    ) => {
      const observer = initErrorScrollObserver();

      // バリエーションチェックを飛ばす
      if (temSave) {
        clearErrors();
        goToSubmit(getValues(), temSave);
        observer.disconnect();
        return;
      }

      handleSubmit((data) => {
        goToSubmit(data, temSave, isRefSubmit);
        if (Object.keys(errors).length === 0) {
          observer.disconnect();
        }
      })(e);
    };

    const goToSubmit = (
      data: any,
      temSave?: boolean,
      isRefSubmit?: boolean
    ) => {
      // TkTransferCodeTextの状態が利用しないの場合、undefinedに変更する
      if (data && data.transferCode && data.transferCode === "□") {
        data.transferCode = undefined;
      }

      if (formContext.preventSubmit && !temSave) {
        for (const key in formContext.preventSubmit) {
          if (formContext.preventSubmit[key]) {
            return;
          }
        }
      }

      onCheckAndSubmit &&
        onCheckAndSubmit(data, submitSource.current, isRefSubmit);
    };

    useEffect(() => {
      setErrorMsgList(errors);
    }, [errors]);

    useEffect(() => {
      temSaveFlagRef && clearErrors();
    }, [temSaveFlagRef, submitSource, clearErrors]);

    useEffect(() => {
      list.forEach((item) => {
        const value = getValues(item.keyword);
        unregister(item.keyword);
        register(item.keyword, ruleSchema[item.keyword]);
        setValue(item.keyword, value);
      });
    }, [ruleSchema, list, register, setValue, getValues, unregister]);

    return (
      <>
        <form
          data-testid="tkFakeTableLineInTkFakeTableForTest"
          ref={submitFrom}
        >
          {list.map((item, index) => {
            return (
              <Controller
                key={`${index}-${ruleSchema[item.keyword]}`}
                name={item.keyword}
                control={control}
                rules={ruleSchema[item.keyword]}
                render={({ field }) => (
                  <TkFakeTableLine
                    control={control}
                    keyword={item.keyword}
                    errorsList={errorMsgList}
                    {...item}
                    {...field}
                    name={item.keyword}
                    value={field.value}
                  />
                )}
              ></Controller>
            );
          })}

          <Box className="tkBottonBox">
            {buttonGroup}

            {!submitButtonGroup && showButton && (
              <TkButton
                ml={!!buttonGroup}
                type="button"
                data-testid="tkFakeTableSumbitButtonForTest"
                onClick={() => {
                  submitSource.current = 0;
                  preventSubmission(submitFrom.current, false);
                }}
                disabled={disabled}
              >
                {submitButtonText}
              </TkButton>
            )}

            {!!submitButtonGroup?.length &&
              submitButtonGroup.map((item, index) => {
                const {
                  submitButtonText,
                  submitButtonDisabled,
                  bypassValidation = false,
                  showButton,
                } = item;

                if (showButton === false) return <React.Fragment key={index} />;

                return (
                  <TkButton
                    key={index}
                    ml={!!buttonGroup || !!index}
                    onClick={() => {
                      submitSource.current = index;
                      setTemSaveFlagRef(() => bypassValidation);
                      preventSubmission(submitFrom.current, bypassValidation);
                    }}
                    type="button"
                    data-testid="tkFakeTableSumbitButtonForTest"
                    disabled={submitButtonDisabled}
                  >
                    {submitButtonText}
                  </TkButton>
                );
              })}
          </Box>
        </form>
      </>
    );
  }
);

export default TkFakeTable;
